import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Document Types API service
 */
export const DocumentTypeApi = {
  /**
   * Get all document types
   * @returns {Promise<Response>} All document types response
   */
  getDocumentTypes: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types`, {
      method: 'GET',
    });
  },

  /**
   * Get document type by ID
   * @param {string|number} id - Document type ID
   * @returns {Promise<Response>} Document type response
   */
  getDocumentType: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Get document type by name
   * @param {string} name - Document type name
   * @returns {Promise<Response>} Document type response
   */
  getDocumentTypeByName: async (name) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/name/${encodeURIComponent(name)}`, {
      method: 'GET',
    });
  },

  /**
   * Create document type
   * @param {Object} data - Document type data
   * @returns {Promise<Response>} Create response
   */
  createDocumentType: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update document type
   * @param {string|number} id - Document type ID
   * @param {Object} data - Updated document type data
   * @returns {Promise<Response>} Update response
   */
  updateDocumentType: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete document type
   * @param {string|number} id - Document type ID
   * @returns {Promise<Response>} Delete response
   */
  deleteDocumentType: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get metadata definitions assigned to document type
   * @param {string|number} documentTypeId - Document type ID
   * @returns {Promise<Response>} Assigned metadata definitions response
   */
  getDocumentTypeMetadataDefinitions: async (documentTypeId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions`, {
      method: 'GET',
    });
  },

  /**
   * Get required metadata definitions for document type
   * @param {string|number} documentTypeId - Document type ID
   * @returns {Promise<Response>} Required metadata definitions response
   */
  getRequiredDocumentTypeMetadataDefinitions: async (documentTypeId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/required-metadata-definitions`, {
      method: 'GET',
    });
  },

  /**
   * Assign metadata definition to document type
   * @param {string|number} documentTypeId - Document type ID
   * @param {Object} data - Assignment data { metadataDefinitionId, required }
   * @returns {Promise<Response>} Assignment response
   */
  assignMetadataDefinitionToDocumentType: async (documentTypeId, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Assign required metadata definition to document type
   * @param {string|number} documentTypeId - Document type ID
   * @param {Object} data - Assignment data { metadataDefinitionId, required: true }
   * @returns {Promise<Response>} Assignment response
   */
  assignRequiredMetadataDefinitionToDocumentType: async (documentTypeId, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, required: true }),
    });
  },

  /**
   * Update document type metadata definition (Admin only)
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} metadataDefId - Metadata definition ID
   * @param {Object} data - Updated metadata definition data
   * @returns {Promise<Response>} Update response
   */
  updateDocumentTypeMetadataDefinition: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/metadata-definitions/document/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete document type metadata definition using relationship ID
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} relationshipId - The relationship ID (not metadataDefinitionId)
   * @returns {Promise<Response>} Delete response
   */
  deleteDocumentTypeMetadataDefinition: async (documentTypeId, relationshipId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions/${relationshipId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Remove metadata definition from document type using relationship ID
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} relationshipId - The relationship ID (not metadataDefinitionId)
   * @returns {Promise<Response>} Remove response
   */
  removeMetadataDefinitionFromDocumentType: async (documentTypeId, relationshipId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions/${relationshipId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Update document type metadata definition assignment using relationship ID
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} relationshipId - The relationship ID (not metadataDefinitionId)
   * @param {Object} data - Updated assignment data { isRequired, ordering, etc. }
   * @returns {Promise<Response>} Update response
   */
  updateDocumentTypeMetadataDefinitionRelationship: async (documentTypeId, relationshipId, data) => {
    // Build the complete relationship object
    const relationshipData = {
      id: relationshipId,
      documentTypeId: parseInt(documentTypeId),
      metadataDefinitionId: data.metadataDefinitionId || data.metadataDefinitionId,
      isRequired: data.isRequired !== undefined ? data.isRequired : false,
      ordering: data.ordering || null,
      ...data // Include any additional fields passed in
    };

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions/${relationshipId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(relationshipData),
    });
  },

  /**
   * Set metadata definition as required for document type using relationship ID
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} relationshipId - The relationship ID (not metadataDefinitionId)
   * @param {string|number} metadataDefinitionId - The metadata definition ID
   * @returns {Promise<Response>} Update response
   */
  setMetadataDefinitionAsRequired: async (documentTypeId, relationshipId, metadataDefinitionId) => {
    const relationshipData = {
      id: relationshipId,
      documentTypeId: parseInt(documentTypeId),
      metadataDefinitionId: parseInt(metadataDefinitionId),
      isRequired: true,
      ordering: null
    };

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions/${relationshipId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(relationshipData),
    });
  },

  /**
   * Set metadata definition as not required for document type using relationship ID
   * @param {string|number} documentTypeId - Document type ID
   * @param {string|number} relationshipId - The relationship ID (not metadataDefinitionId)
   * @param {string|number} metadataDefinitionId - The metadata definition ID
   * @returns {Promise<Response>} Update response
   */
  setMetadataDefinitionAsNotRequired: async (documentTypeId, relationshipId, metadataDefinitionId) => {
    const relationshipData = {
      id: relationshipId,
      documentTypeId: parseInt(documentTypeId),
      metadataDefinitionId: parseInt(metadataDefinitionId),
      isRequired: false,
      ordering: null
    };

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-types/${documentTypeId}/metadata-definitions/${relationshipId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(relationshipData),
    });
  }
};